import {
  getAlerts,
  getMagicLinkPatientProfileData,
  getPatientProcedures,
  getPatientProfileData,
  getTasks
} from 'src/api/endpoints/userWeb';
import {getEocSummaries, getPatientStatisticSummaries} from 'src/api/endpoints/statisticsWeb';
import {getMessages} from 'src/api/endpoints/messagesWeb';
import {getPatientConfig} from 'src/api/endpoints/patientWeb';
import {handleApiRequest} from 'src/api/handleApiRequest';
import {custom_moment} from '@/plugins/moment-cultured';
import {type Task} from 'src/types/webContracts';
import {factorProcedures} from 'src/utils/procedureUtils';
import {EventCompletionStatus} from '@/constants/enums';
import type {useAiSummariesStore} from "@/store";

export const loadPatientInfoAndTasks = async (sessionStore, messagesStore, dashboardStore, aiSummariesStore: ReturnType<typeof useAiSummariesStore>, $q) => {
  document.querySelectorAll('html')[0].scrollTop = 0;
  var isMagicLinkUser = sessionStore.isMagicLinkUser;

  sessionStore.setIsHeaderVisible(true);
  sessionStore.setIsFooterVisible(true);

  //this could hit because of a login or a relogin
  if (!dashboardStore.patient && !dashboardStore.isLoading) {
    if (!dashboardStore.isLoading) {
      dashboardStore.setIsLoading(true);
      messagesStore.setIsLoading(true);

      const isEocLogin = sessionStore.user.selectedEocId > 0;
      const tasks = new Array<Task>();

      let promises = [
        handleApiRequest(() => isMagicLinkUser ? getMagicLinkPatientProfileData() : getPatientProfileData(isEocLogin), null, $q),
        handleApiRequest(() => getPatientConfig(), null, $q),
        handleApiRequest(() => getPatientProcedures(), null, $q)
      ]

      if (isEocLogin && !isMagicLinkUser) {
        const eocBasedPromises = [
          handleApiRequest(() => getAlerts(), null, $q),
          handleApiRequest(() => getTasks(EventCompletionStatus.All), null, $q),
          handleApiRequest(() => getMessages(), null, $q),
          handleApiRequest(() => getPatientStatisticSummaries('Wearable'), null, $q),
        ]
        promises = [...promises,
        ...eocBasedPromises
        ]
      }

      const responses = await Promise.all(promises);
      let
        patientResponse,
        patientConfigResponse,
        getPatientProcRsp,
        alertResponse,
        taskResponse,
        messagesResponse,
        statsResponse;

      patientResponse = responses[0]
      patientConfigResponse = responses[1]
      getPatientProcRsp = responses[2]

      if (isMagicLinkUser) {
        taskResponse = await handleApiRequest(() => getTasks(EventCompletionStatus.NotCompleted), null, $q);
        const loadTasks = [...taskResponse.result.tasks];

        tasks.push(...loadTasks);
        dashboardStore.loadNextTaskDates(taskResponse.result);
        dashboardStore.loadTasks(tasks);
      }

      if (patientResponse) {
        dashboardStore.loadPatient(patientResponse.result);
      }

      if (patientConfigResponse) {
        dashboardStore.loadPatientConfig(patientConfigResponse.result);
      }

      if (getPatientProcRsp?.result) {
        const episodesOfCare = getPatientProcRsp.result.episodesOfCare;

        factorProcedures(episodesOfCare, false, isMagicLinkUser);

        sessionStore.loadProcedures(episodesOfCare);
      }



      if (responses.length > 3) {
        alertResponse = responses[3]
        taskResponse = responses[4]
        messagesResponse = responses[5]
        statsResponse = responses[6]

        if (alertResponse) {
          dashboardStore.loadAlerts(alertResponse.result);
        }

        if (taskResponse) {
          const loadTasks = [...taskResponse.result.tasks];

          tasks.push(...loadTasks);
          dashboardStore.loadNextTaskDates(taskResponse.result);
        }

        dashboardStore.loadTasks(tasks);

        if (messagesResponse) {
          messagesStore.setUserId(sessionStore.userId);
          messagesStore.loadMessages(messagesResponse.result);
        }
        messagesStore.setIsLoading(false);

        if (statsResponse) {
          dashboardStore.loadPatientWearables(statsResponse.result);
        }
      }
      
      // EOC AI Summaries
      if (sessionStore.selectedProcedure?.eocId && dashboardStore.patientConfig?.features.isAIPatientSummariesEnabled) {
        const eocSummariesResponse = await handleApiRequest(() => getEocSummaries(sessionStore.selectedProcedure.eocId), null, $q);
        
        if (eocSummariesResponse) {
          aiSummariesStore.setAiSummariesStore(eocSummariesResponse.result);
        }
      }

      dashboardStore.setIsLoading(false);
    }
  }
}

export const loadPatientInfo = async (sessionStore, dashboardStore, $q) => {
  const isMagicLinkUser = sessionStore.isMagicLinkUser;
  const isEocLogin = sessionStore.user.selectedEocId > 0;

  const patientResponse = await handleApiRequest(
    () => (isMagicLinkUser ? getMagicLinkPatientProfileData() : getPatientProfileData(isEocLogin)),
    null,
    $q
  );

  if (patientResponse) {
    dashboardStore.loadPatient(patientResponse.result);
  }
};

export const loadPatientConfig = async (dashboardStore, $q) => {
  const patientConfigResponse = await handleApiRequest(
    () => getPatientConfig(),
    null,
    $q
  );

  if (patientConfigResponse) {
    dashboardStore.loadPatientConfig(patientConfigResponse.result);
  }
};

// const unreadMessagesreCount = computed(() => messagesStore.unreadMessagesCount);

const taskCoolDownTimer = (dashboardStore, $q) => {
  const now = custom_moment(),
    released = custom_moment().add(60 - dashboardStore.mostRecentCompletedTime, 'minute')

  let runningTimer = 0;

  clearTimeout(runningTimer);
  if (now.isBefore(released)) {
    runningTimer = window.setTimeout(async function () {
      refreshTasks(dashboardStore, $q);
    }, (60 - dashboardStore.mostRecentCompletedTime) * 60 * 1000);
  }
}

const refreshTasks = async (dashboardStore, $q) => {
  dashboardStore.setIsLoading(true);
  const taskResponse = await handleApiRequest(() => getTasks(), null, $q);

  if (taskResponse) {
    const loadTasks = [...taskResponse.result.tasks];

    dashboardStore.loadTasks(loadTasks);
    dashboardStore.loadNextTaskDates(taskResponse.result);
  }
  dashboardStore.setIsLoading(false);
}
